import React, { useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
} from "@mui/material";
import { styled } from "@mui/system";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

// Custom styled components
const CustomTableCell = styled(TableCell)(({ columnWidth }) => ({
  backgroundColor: "#424752",
  fontSize: "13px",
  textAlign: "center",
  color: "white",
  border: "1px solid #e0e0e0",
  padding: "8px 12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyTableCell = styled(CustomTableCell)(({ left, isLastSticky }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 3,
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

const CustomTableRow = styled(TableRow)(({ index }) => ({
  backgroundColor: index % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
}));

const BodyTableCell = styled(TableCell)(({ columnWidth }) => ({
  border: "0px solid #e0e0e0",
  padding: "8px 12px",
  textAlign: "center",
  fontSize: "12px",
  minWidth: columnWidth || 100,
  maxWidth: columnWidth || 100,
  whiteSpace: "normal",
  wordWrap: "break-word",
  overflow: "hidden",
  textOverflow: "ellipsis",
}));

const StickyBodyTableCell = styled(BodyTableCell)(({ left, isLastSticky, rowIndex }) => ({
  position: "sticky",
  left: left || 0,
  zIndex: 1,
  backgroundColor: rowIndex % 2 === 0 ? "#FFFFFF" : "#F7F7F7",
  boxShadow: isLastSticky ? "2px 0 4px rgba(0,0,0,0.2)" : "none",
}));

// Helper function to format date
const formatDate = (date) => {
  if (!date) return "";
  try {
    const d = new Date(date);
    return d.toISOString().split("T")[0];
  } catch {
    return "";
  }
};

// Helper function to estimate text width with a cap
const estimateTextWidth = (text) => {
  if (!text) return 100;
  const charWidth = 8;
  const padding = 24;
  const estimatedWidth = text.length * charWidth + padding;
  return Math.min(estimatedWidth, 8 * window.innerWidth / 100);
};

// Helper function to generate all dates in range
const getDateRange = (startDate, endDate) => {
  const dates = [];
  if (!startDate || !endDate) return dates;
  const start = new Date(startDate);
  const end = new Date(endDate);
  if (isNaN(start.getTime()) || isNaN(end.getTime())) return [];
  for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
    dates.push(formatDate(new Date(d)));
  }
  return dates;
};

// Human-readable labels for parameters
const parameterLabels = {
  chamber1Temp: "Chamber 1 Temp",
  chamber2Temp: "Chamber 2 Temp",
  coolingTime: "Cooling Time",
  remarks: "Remarks",
  totalCycleTime: "Total Cycle Time",
  vacuumTimeB1: "Vacuum Time B1",
  vacuumTimeB2: "Vacuum Time B2",
};

// Ordered parameters for rows
const orderedParameters = [
  "chamber1Temp",
  "chamber2Temp",
  "vacuumTimeB1",
  "vacuumTimeB2",
  "totalCycleTime",
  "coolingTime",
  "remarks",
];

const LaminationMachineTable = ({ data, startDate, endDate, shifts, subForm }) => {
  // Debug: Log received props
  console.log("LaminationMachineTable props:", { data, startDate, endDate, shifts, subForm });

  // Get all dates in the range
  const dates = getDateRange(startDate, endDate);

  // Use provided shifts, fallback to data if shifts prop is empty
  const effectiveShifts = shifts.length > 0 ? shifts : [
    ...new Set(data.map((item) => item?.inspectionShift).filter(Boolean))
  ].sort();
  console.log("Effective Shifts:", effectiveShifts);

  // Collect all lines and laminators from data
  const allLines = useMemo(() => {
    const lines = new Set();
    data.forEach((item) => {
      Object.keys(item.form_data || {}).forEach((line) => lines.add(line));
    });
    return [...lines].sort();
  }, [data]);

  const allLaminators = useMemo(() => {
    const laminators = new Set();
    data.forEach((item) => {
      Object.values(item.form_data || {}).forEach((lineData) => {
        Object.keys(lineData).forEach((laminator) => laminators.add(laminator));
      });
    });
    return [...laminators].sort();
  }, [data]);

  // Build header structure
  const headerStructure = useMemo(() => {
    const structure = [];
    dates.forEach((date) => {
      if (!startDate || !endDate || (date >= formatDate(startDate) && date <= formatDate(endDate))) {
        const dateNode = { date, shifts: [] };
        effectiveShifts.forEach((shift) => {
          const shiftNode = { shift, lines: [] };
          allLines.forEach((line) => {
            const lineNode = { line, laminators: [] };
            allLaminators.forEach((laminator) => {
              lineNode.laminators.push({ laminator, id: `${date}_${shift}_${line}_${laminator}` });
            });
            shiftNode.lines.push(lineNode);
          });
          dateNode.shifts.push(shiftNode);
        });
        structure.push(dateNode);
      }
    });
    return structure;
  }, [dates, effectiveShifts, allLines, allLaminators, startDate, endDate]);

  // Calculate column widths
  const columnWidths = useMemo(() => {
    const widths = {
      parameter: estimateTextWidth("Parameter"),
      value: 100, // Default width for value columns
    };

    // Width for parameter column
    orderedParameters.forEach((param) => {
      widths.parameter = Math.min(
        Math.max(widths.parameter, estimateTextWidth(parameterLabels[param] || param)),
        8 * window.innerWidth / 100
      );
    });

    // Width for value columns
    data.forEach((item) => {
      Object.entries(item.form_data || {}).forEach(([lineKey, lineData]) => {
        Object.entries(lineData).forEach(([laminatorKey, laminatorData]) => {
          Object.entries(laminatorData).forEach(([param, value]) => {
            widths.value = Math.min(
              Math.max(widths.value, estimateTextWidth(String(value))),
              8 * window.innerWidth / 100
            );
          });
        });
      });
    });

    // Assign widths to each laminator column
    headerStructure.forEach((dateNode) => {
      dateNode.shifts.forEach((shiftNode) => {
        shiftNode.lines.forEach((lineNode) => {
          lineNode.laminators.forEach((laminator) => {
            widths[laminator.id] = widths.value;
          });
        });
      });
    });

    return widths;
  }, [data, headerStructure]);

  // Calculate left position for sticky column
  const getLeftPosition = (index) => {
    return index === 0 ? 0 : columnWidths.parameter;
  };

  // Prepare table rows
  const tableRows = useMemo(() => {
    const rows = orderedParameters.map((param) => ({
      parameter: parameterLabels[param] || param,
      data: {},
    }));

    data.forEach((item) => {
      const date = formatDate(item.inspectionDate);
      const shift = item.inspectionShift;
      Object.entries(item.form_data || {}).forEach(([line, lineData]) => {
        Object.entries(lineData).forEach(([laminator, laminatorData]) => {
          const key = `${date}_${shift}_${line}_${laminator}`;
          orderedParameters.forEach((param, index) => {
            rows[index].data[key] = laminatorData[param] || "N/A";
          });
        });
      });
    });

    return rows;
  }, [data]);

  // Calculate colSpan for headers
  const getDateColSpan = (dateNode) => {
    return dateNode.shifts.reduce(
      (sum, shiftNode) =>
        sum +
        shiftNode.lines.reduce(
          (lineSum, lineNode) => lineSum + lineNode.laminators.length,
          0
        ),
      0
    );
  };

  const getShiftColSpan = (shiftNode) => {
    return shiftNode.lines.reduce(
      (sum, lineNode) => sum + lineNode.laminators.length,
      0
    );
  };

  const getLineColSpan = (lineNode) => {
    return lineNode.laminators.length;
  };

  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();
    const merges = [];

    // Row 1: Dates
    const row1 = ["Parameter"];
    let colIdx = 1;

    headerStructure.forEach((dateNode) => {
      const span = getDateColSpan(dateNode);
      if (span > 0) {
        row1.push(dateNode.date);
        if (span > 1) {
          merges.push({ s: { r: 0, c: colIdx }, e: { r: 0, c: colIdx + span - 1 } });
        }
        colIdx += span;
      }
    });

    // Row 2: Shifts
    const row2 = ["Parameter"];
    colIdx = 1;
    headerStructure.forEach((dateNode) => {
      dateNode.shifts.forEach((shiftNode) => {
        const span = getShiftColSpan(shiftNode);
        if (span > 0) {
          row2.push(`${shiftNode.shift}`);
          if (span > 1) {
            merges.push({ s: { r: 1, c: colIdx }, e: { r: 1, c: colIdx + span - 1 } });
          }
          colIdx += span;
        }
      });
    });

    // Row 3: Lines
    const row3 = ["Parameter"];
    colIdx = 1;
    headerStructure.forEach((dateNode) => {
      dateNode.shifts.forEach((shiftNode) => {
        shiftNode.lines.forEach((lineNode) => {
          const span = getLineColSpan(lineNode);
          if (span > 0) {
            row3.push(lineNode.line);
            if (span > 1) {
              merges.push({ s: { r: 2, c: colIdx }, e: { r: 2, c: colIdx + span - 1 } });
            }
            colIdx += span;
          }
        });
      });
    });

    // Row 4: Laminator names (final header)
    const row4 = ["Parameter"];
    headerStructure.forEach((dateNode) => {
      dateNode.shifts.forEach((shiftNode) => {
        shiftNode.lines.forEach((lineNode) => {
          lineNode.laminators.forEach((lam) => {
            row4.push(lam.laminator);
          });
        });
      });
    });

    // Data rows
    const dataRows = tableRows.map((row) => {
      const r = [row.parameter];
      headerStructure.forEach((dateNode) => {
        dateNode.shifts.forEach((shiftNode) => {
          shiftNode.lines.forEach((lineNode) => {
            lineNode.laminators.forEach((lam) => {
              r.push(row.data[lam.id] || "N/A");
            });
          });
        });
      });
      return r;
    });

    const sheetData = [row1, row2, row3, row4, ...dataRows];
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    // Apply all merges
    if (merges.length > 0) ws["!merges"] = merges;

    // Column widths
    ws["!cols"] = [
      { wch: 35 }, // Parameter
      ...Array(row4.length - 1).fill({ wch: 18 }),
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Lamination Machine");

    const fileName = `Lamination_Machine_${formatDate(startDate || new Date())}_to_${formatDate(endDate || new Date())}.xlsx`;
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });

    saveAs(new Blob([excelBuffer], { type: "application/octet-stream" }), fileName);
  };

  return (
    <div style={{ margin: "20px", overflowX: "auto", maxWidth: "100vw" }}>
      <Typography variant="h6" gutterBottom>
        Lamination Machine Parameters Verification
      </Typography>

      <Button
        variant="contained"
        color="success"
        onClick={exportToExcel}
        sx={{ mb: 2 }}
        disabled={tableRows.length === 0 || headerStructure.length === 0}
      >
        Download as Excel
      </Button>

      <TableContainer component={Paper} sx={{ maxWidth: "100%", overflowX: "auto" }}>
        <Table sx={{ tableLayout: "fixed", width: "max-content" }}>
          <TableHead>
            <TableRow>
              <StickyTableCell
                rowSpan={3}
                columnWidth={columnWidths.parameter}
                left={getLeftPosition(0)}
                isLastSticky={true}
              >
                Parameter
              </StickyTableCell>
              {headerStructure.map((dateNode) => (
                <CustomTableCell
                  key={dateNode.date}
                  colSpan={getDateColSpan(dateNode)}
                  columnWidth={getDateColSpan(dateNode) * columnWidths.value}
                >
                  {dateNode.date}
                </CustomTableCell>
              ))}
            </TableRow>
            <TableRow>
              {headerStructure.map((dateNode) =>
                dateNode.shifts.map((shiftNode) => (
                  <CustomTableCell
                    key={`${dateNode.date}_${shiftNode.shift}`}
                    colSpan={getShiftColSpan(shiftNode)}
                    columnWidth={getShiftColSpan(shiftNode) * columnWidths.value}
                  >
                    {shiftNode.shift}
                  </CustomTableCell>
                ))
              )}
            </TableRow>
            <TableRow>
              {headerStructure.map((dateNode) =>
                dateNode.shifts.map((shiftNode) =>
                  shiftNode.lines.map((lineNode) => (
                    <React.Fragment key={`${dateNode.date}_${shiftNode.shift}_${lineNode.line}`}>
                      {lineNode.laminators.map((laminator, index) => (
                        <CustomTableCell
                          key={laminator.id}
                          columnWidth={columnWidths[laminator.id]}
                        >
                          {index === 0 ? `${lineNode.line} ${laminator.laminator}` : laminator.laminator}
                        </CustomTableCell>
                      ))}
                    </React.Fragment>
                  ))
                )
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.length === 0 ? (
              <CustomTableRow index={0}>
                <StickyBodyTableCell
                  colSpan={
                    1 +
                    headerStructure.reduce(
                      (sum, dateNode) => sum + getDateColSpan(dateNode),
                      0
                    )
                  }
                  align="center"
                  rowIndex={0}
                  columnWidth={columnWidths.parameter}
                  left={getLeftPosition(0)}
                  isLastSticky={true}
                >
                  No data available
                </StickyBodyTableCell>
              </CustomTableRow>
            ) : (
              tableRows.map((row, index) => (
                <CustomTableRow key={index} index={index}>
                  <StickyBodyTableCell
                    rowIndex={index}
                    columnWidth={columnWidths.parameter}
                    left={getLeftPosition(0)}
                    isLastSticky={true}
                  >
                    {row.parameter}
                  </StickyBodyTableCell>
                  {headerStructure.map((dateNode) =>
                    dateNode.shifts.map((shiftNode) =>
                      shiftNode.lines.map((lineNode) =>
                        lineNode.laminators.map((laminator) => (
                          <BodyTableCell
                            key={`${laminator.id}_${index}`}
                            columnWidth={columnWidths[laminator.id]}
                          >
                            {row.data[laminator.id] || "N/A"}
                          </BodyTableCell>
                        ))
                      )
                    )
                  )}
                </CustomTableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default LaminationMachineTable;